import { assembleReview, ReviewAssemblyError, ReviewAssemblyErrorCode } from 'modules/match-video/domain/assembler';
import { ReviewFrameProvider, ReviewTimeline } from 'modules/match-video/domain/interfaces';
import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadBlob } from 'shared/utils/download-blob';

export type ReviewAssemblyStatus = 'idle' | 'assembling' | 'ready' | 'failed';

export type ReviewAssemblyFailure = { code: ReviewAssemblyErrorCode; clipName?: string } | { code: 'unknown' };

export interface ReviewAssembly {
  status: ReviewAssemblyStatus;
  /** 0…1 while assembling. */
  progress: number;
  failure: ReviewAssemblyFailure | null;
  /** Size of the ready MP4 in bytes. */
  resultSize: number | null;
  /**
   * Resolves when the assembly ends in any way — the button that started it stays locked until then. `frames` —
   * the intro and plates (`createReviewFrameProvider`); without it the intro is black and there are no plates.
   */
  assemble: (timeline: ReviewTimeline, frames?: ReviewFrameProvider) => Promise<void>;
  cancel: () => void;
  /** Saves the MP4 and drops it from memory: nothing of the review is kept after the download. */
  download: (fileName: string) => void;
  reset: () => void;
}

/**
 * Runs one review assembly at a time. The result lives only in this hook (a `Blob` is not Redux state) and is
 * released on download, on reset and when the screen unmounts — a running assembly is cancelled then too.
 */
export function useReviewAssembly(): ReviewAssembly {
  const [status, setStatus] = useState<ReviewAssemblyStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [failure, setFailure] = useState<ReviewAssemblyFailure | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const resultRef = useRef<Blob | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    resultRef.current = null;
    setResultSize(null);
    setFailure(null);
    setProgress(0);
    setStatus('idle');
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      resultRef.current = null;
    },
    [],
  );

  const assemble = useCallback(async (timeline: ReviewTimeline, frames?: ReviewFrameProvider) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    resultRef.current = null;
    setResultSize(null);
    setFailure(null);
    setProgress(0);
    setStatus('assembling');

    try {
      const blob = await assembleReview({ timeline, frames, signal: controller.signal, onProgress: setProgress });
      if (controller.signal.aborted) return;
      resultRef.current = blob;
      setResultSize(blob.size);
      setStatus('ready');
    } catch (error) {
      if (controller.signal.aborted) {
        setStatus('idle');
        setProgress(0);
        return;
      }
      console.error(error);
      setFailure(error instanceof ReviewAssemblyError ? { code: error.code, clipName: error.clipName } : { code: 'unknown' });
      setStatus('failed');
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, []);

  const download = useCallback(
    (fileName: string) => {
      if (!resultRef.current) return;
      downloadBlob(resultRef.current, fileName);
      reset();
    },
    [reset],
  );

  return { status, progress, failure, resultSize, assemble, cancel, download, reset };
}
